// IF (11/30/2020): Sorting by sendInterval descending, if the sendInterval is the same for two
// communications, we show the non-recurring ones first
import { AUTOMATION_TRIGGER_TYPES } from './automation-trigger-types';

import { compareTimeZoneAdjustedTime, SORT_ORDER } from '../util/sort-functions';

export const sortStartDateTriggeredCommunications = (
  startDateTriggeredCommunications: Array<StartDateTriggeredCommunication>,
) => {
  // IF (12/04/20): This function is just used for sorting, it roughly converts intervals into days
  // for comparison
  const convertIntervalToRelativeDays = ({
    sendInterval,
    sendIntervalType,
  }: StartDateTriggeredCommunication) => {
    if (sendInterval === null) {
      throw new Error('sendInterval required');
    }

    if (sendIntervalType === 'DAY') {
      return sendInterval;
    }
    if (sendIntervalType === 'WEEK') {
      return sendInterval * 7;
    }
    if (sendIntervalType === 'MONTH') {
      return sendInterval * 31;
    }
    if (sendIntervalType === 'YEAR') {
      return sendInterval * 365;
    }

    throw new Error('Invalid sendIntervalType');
  };

  return startDateTriggeredCommunications.sort((a, b) => {
    const convertedA = convertIntervalToRelativeDays(a);
    const convertedB = convertIntervalToRelativeDays(b);

    if (convertedA === convertedB) {
      // if the interval is equal, check if they have are both recurring, or both not recurring as we section recurring automations separately
      if ((a.isRecurring && b.isRecurring) || (!a.isRecurring && !b.isRecurring)) {
        // sort automations within a step by sendAt in utc to reflects the order they would send, i.e. 11am Eastern is displayed before 11am Central
        return compareTimeZoneAdjustedTime(
          { time: a.sendAt, timeZoneId: a.timeZoneId },
          { time: b.sendAt, timeZoneId: b.timeZoneId },
          SORT_ORDER.ASC,
        );
      }

      return a.isRecurring ? 1 : -1;
    }

    return convertedA > convertedB ? 1 : -1;
  });
};

export const getAutomationsByInterval = (automations: Array<StartDateTriggeredCommunication>) =>
  automations.reduce<
    Array<{
      sendInterval: number;
      sendIntervalType: StartDateTriggeredCommunicationIntervalType;
      recurrenceInterval: number | null;
      recurrenceIntervalType: StartDateTriggeredCommunicationIntervalType | null;
      isRecurring: boolean;
      triggerType: StartDateTriggeredCommunicationTriggerType;
      automations: Array<StartDateTriggeredCommunication>;
    }>
  >((acc, automation) => {
    const existingCommunication = acc.find(
      (a) =>
        a.sendInterval === automation.sendInterval &&
        a.sendIntervalType === automation.sendIntervalType &&
        a.isRecurring === automation.isRecurring &&
        a.triggerType === automation.triggerType,
    );
    if (existingCommunication) {
      existingCommunication.automations.push(automation);
    } else {
      acc.push({
        sendInterval: automation.sendInterval,
        sendIntervalType: automation.sendIntervalType,
        recurrenceInterval: automation.recurrenceInterval,
        recurrenceIntervalType: automation.recurrenceIntervalType,
        isRecurring: automation.isRecurring,
        triggerType: automation.triggerType,
        automations: [automation],
      });
    }

    return acc;
  }, []);

export const formattedSendInterval = ({
  isRecurring,
  sendInterval,
  sendIntervalType,
  recurrenceInterval,
  recurrenceIntervalType,
  triggerType,
}: {
  isRecurring: boolean;
  sendInterval: number;
  sendIntervalType: StartDateTriggeredCommunicationIntervalType;
  recurrenceInterval: number | null;
  recurrenceIntervalType: StartDateTriggeredCommunicationIntervalType | null;
  triggerType: StartDateTriggeredCommunicationTriggerType;
}) => {
  if (sendInterval === null || !sendIntervalType) {
    throw new Error('Missing sendInterval or sendIntervalType, cannot format');
  }

  const triggerTypeText = `An Employee ${
    triggerType === AUTOMATION_TRIGGER_TYPES.START_DATE ? 'Start Day' : 'Birthday'
  }`;
  const isSendingAnnually = sendingAnnually({
    isRecurring,
    recurrenceInterval,
    recurrenceIntervalType,
  });
  const isOffsetAndRecurrenceSame = offsetAndRecurrenceSame({
    sendInterval,
    sendIntervalType,
    recurrenceInterval,
    recurrenceIntervalType,
  });

  if (sendInterval === 0 && !isRecurring) {
    return `On ${triggerTypeText}`;
  }
  if (isSendingAnnually && isOffsetAndRecurrenceSame) {
    return `Every Year On ${triggerTypeText}`;
  }

  const showEveryWhenRecurring = `${isRecurring ? 'Every ' : ''}${
    isSendingAnnually && !isOffsetAndRecurrenceSame ? 'Year ' : ''
  }`;
  const showIntervalIfGreaterThanOne =
    sendInterval === 1 && isRecurring ? '' : `${Math.abs(sendInterval)} `;
  const formattedSendIntervalType =
    sendIntervalType.charAt(0) + sendIntervalType.slice(1).toLowerCase();
  const potentiallyPluralIntervalType = `${formattedSendIntervalType}${
    Math.abs(sendInterval) === 1 ? '' : 's'
  }`;
  const sendOffset = sendOffsetType({
    isRecurring,
    sendInterval,
    sendIntervalType,
    recurrenceInterval,
    recurrenceIntervalType,
  });
  const showOffsetTypeAfterBefore = sendOffset === 'Before' ? ' Before ' : ' After ';

  return `${showEveryWhenRecurring}${showIntervalIfGreaterThanOne}${potentiallyPluralIntervalType}${showOffsetTypeAfterBefore}${triggerTypeText}`;
};

export const offsetAndRecurrenceSame = ({
  sendInterval,
  sendIntervalType,
  recurrenceInterval,
  recurrenceIntervalType,
}: {
  sendInterval: number;
  sendIntervalType: StartDateTriggeredCommunicationIntervalType;
  recurrenceInterval: number | null;
  recurrenceIntervalType: StartDateTriggeredCommunicationIntervalType | null;
}) =>
  Boolean(
    sendInterval &&
      sendInterval === recurrenceInterval &&
      sendIntervalType &&
      sendIntervalType === recurrenceIntervalType,
  );

export const sendingAnnually = ({
  isRecurring,
  recurrenceInterval,
  recurrenceIntervalType,
}: {
  isRecurring: boolean;
  recurrenceInterval: number | null;
  recurrenceIntervalType: StartDateTriggeredCommunicationIntervalType | null;
}) => isRecurring && recurrenceInterval === 1 && recurrenceIntervalType === 'YEAR';

export const isRecurringNonAnnually = ({
  isRecurring,
  sendInterval,
  sendIntervalType,
  recurrenceInterval,
  recurrenceIntervalType,
}: {
  isRecurring: boolean;
  sendInterval: number;
  sendIntervalType: StartDateTriggeredCommunicationIntervalType;
  recurrenceInterval: number | null;
  recurrenceIntervalType: StartDateTriggeredCommunicationIntervalType | null;
}) =>
  isRecurring &&
  offsetAndRecurrenceSame({
    sendInterval,
    sendIntervalType,
    recurrenceInterval,
    recurrenceIntervalType,
  });

export const sendOffsetType = ({
  isRecurring,
  sendInterval,
  sendIntervalType,
  recurrenceInterval,
  recurrenceIntervalType,
}: {
  isRecurring: boolean;
  sendInterval: number;
  sendIntervalType: StartDateTriggeredCommunicationIntervalType;
  recurrenceInterval: number | null;
  recurrenceIntervalType: StartDateTriggeredCommunicationIntervalType | null;
}) => {
  if (
    isRecurringNonAnnually({
      isRecurring,
      sendInterval,
      sendIntervalType,
      recurrenceInterval,
      recurrenceIntervalType,
    })
  ) {
    return 'Every';
  }
  return sendInterval < 0 ? 'Before' : 'After';
};

export const getIntervalForAutomations = ({
  isRecurring,
  sendInterval,
  sendIntervalType,
  recurrenceInterval,
  recurrenceIntervalType,
}: {
  isRecurring: boolean;
  sendInterval: number;
  sendIntervalType: StartDateTriggeredCommunicationIntervalType;
  recurrenceInterval: number | null;
  recurrenceIntervalType: StartDateTriggeredCommunicationIntervalType | null;
}): EmployeeAutomationSendInterval => ({
  sendInterval: Math.abs(sendInterval),
  sendIntervalType,
  sendOffset: sendOffsetType({
    isRecurring,
    sendInterval,
    sendIntervalType,
    recurrenceInterval,
    recurrenceIntervalType,
  }),
  sendAnnually: sendingAnnually({ isRecurring, recurrenceInterval, recurrenceIntervalType }),
});
